/**
 * DataTableView Component
 *
 * Thin chatbot-specific wrapper around @axon/ui Table components.
 * Renders structured data query results as an interactive table.
 * Phase 16: Rich Message Rendering
 *
 * Batch 2: numeric columns are right-aligned with tabular figures, and on
 * narrow viewports the table collapses to a stacked label/value card list
 * instead of horizontal scrolling.
 *
 * DRY: Uses Table components from @axon/ui (flat exports), not Chakra compounds.
 * Rule 15: No `any` types. Import from @axon/ui only.
 *
 * @module DataTableView
 */

'use client';

import { memo, useState, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import {
  Box,
  VStack,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableColumnHeader,
  Text,
  Flex,
  Button,
} from '@axon/ui';
import { LuChevronUp, LuChevronDown } from 'react-icons/lu';

import type { TableData } from '../types/chatbot.types';
import { isEntityId } from '../utils/chatbot.link-utils';

import { EntityLink } from './EntityLink';

// ============================================================================
// Constants
// ============================================================================

/** Maximum rows to display before showing "show more" */
const INITIAL_VISIBLE_ROWS = 10;

/** Share of a column's non-empty cells that must be numeric to right-align it. */
const NUMERIC_COLUMN_THRESHOLD = 0.8;

/** UUID v7 pattern: 8-4-4-4-12 hex characters */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Tabular figures so right-aligned numbers line up by digit. */
const TABULAR_NUMS = { fontVariantNumeric: 'tabular-nums' } as const;

/** Truncate UUID values to first 8 chars for compact display */
function formatCellValue(value: string): string {
  if (UUID_PATTERN.test(value)) {
    return value.substring(0, 8) + '...';
  }
  return value;
}

/**
 * A value reads as numeric if, after stripping thousands separators, common
 * currency symbols, percent and whitespace, the remainder parses as a number.
 * Mixed unit strings ("96.3 miles") deliberately stay left-aligned as text.
 */
function isNumericValue(value: string): boolean {
  const cleaned = value.replace(/[\s,%$€₺£]/g, '');
  if (cleaned === '') return false;
  return !Number.isNaN(Number(cleaned));
}

// ============================================================================
// Props
// ============================================================================

interface DataTableViewProps {
  tableData: TableData;
}

// ============================================================================
// Component
// ============================================================================

/**
 * DataTableView component
 *
 * Renders data query results in a compact, styled table.
 *
 * @example
 * <DataTableView tableData={{ headers: ['Name', 'Status'], rows: [['Acme', 'active']] }} />
 */
export const DataTableView = memo(function DataTableView({ tableData }: DataTableViewProps) {
  const t = useTranslations('chatbot');
  const [showAll, setShowAll] = useState(false);

  const { headers, rows } = tableData;
  const hasMoreRows = rows.length > INITIAL_VISIBLE_ROWS;
  const visibleRows = useMemo(
    () => (showAll ? rows : rows.slice(0, INITIAL_VISIBLE_ROWS)),
    [showAll, rows]
  );

  // Per-column numeric detection → right-align + tabular figures.
  const numericColumns = useMemo(
    () =>
      headers.map((_, col) => {
        let total = 0;
        let numeric = 0;
        for (const row of rows) {
          const v = (row[col] ?? '').trim();
          if (!v) continue;
          total += 1;
          if (isNumericValue(v)) numeric += 1;
        }
        return total > 0 && numeric / total >= NUMERIC_COLUMN_THRESHOLD;
      }),
    [headers, rows]
  );

  const renderCell = (cell: string) =>
    isEntityId(cell) ? <EntityLink entityId={cell.trim()} /> : formatCellValue(cell);

  // Empty state: no rows to display → show a muted centered message instead of nothing.
  if (headers.length === 0 || rows.length === 0) {
    return (
      <Box
        borderRadius="md"
        border="1px solid"
        borderColor="border.muted"
        background="bg.panel"
        padding="6"
        textAlign="center"
      >
        <Text fontSize="xs" color="fg.muted">
          {t('dataTable.empty' as Parameters<typeof t>[0])}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      borderRadius="md"
      overflow="hidden"
      border="1px solid"
      borderColor="border.muted"
      background="bg.panel"
      fontSize="xs"
    >
      {/* Table view (md+) */}
      <Box display={{ base: 'none', md: 'block' }} overflowX="auto" maxHeight="300px" overflowY="auto">
        <Table size="sm" variant="outline">
          <TableHeader>
            <TableRow>
              {headers.map((header, i) => (
                <TableColumnHeader
                  key={i}
                  position="sticky"
                  top="0"
                  zIndex={1}
                  paddingX="2"
                  paddingY="1.5"
                  fontSize="xs"
                  fontWeight="semibold"
                  color="fg.muted"
                  whiteSpace="nowrap"
                  background="bg.muted"
                >
                  {numericColumns[i] ? (
                    <Box width="full" css={{ textAlign: 'right' }}>
                      {header}
                    </Box>
                  ) : (
                    header
                  )}
                </TableColumnHeader>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleRows.map((row, rowIdx) => (
              <TableRow key={rowIdx}>
                {row.map((cell, cellIdx) => (
                  <TableCell
                    key={cellIdx}
                    paddingX="2"
                    paddingY="1"
                    fontSize="xs"
                    color="fg.default"
                    maxWidth="200px"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    title={cell}
                  >
                    {numericColumns[cellIdx] ? (
                      <Box width="full" css={{ textAlign: 'right', ...TABULAR_NUMS }}>
                        {renderCell(cell)}
                      </Box>
                    ) : (
                      renderCell(cell)
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {/* Card view (mobile / narrow) — stacked label:value rows, no h-scroll */}
      <Box
        display={{ base: 'block', md: 'none' }}
        maxHeight="300px"
        overflowY="auto"
        padding="2"
      >
        <VStack gap="2" align="stretch">
          {visibleRows.map((row, rowIdx) => (
            <Box
              key={rowIdx}
              borderRadius="sm"
              border="1px solid"
              borderColor="border.muted"
              background="bg.subtle"
              paddingX="2"
              paddingY="1.5"
            >
              {row.map((cell, cellIdx) => (
                <Flex key={cellIdx} justify="between" gap="3" paddingY="0.5" align="baseline">
                  <Text fontSize="xs" color="fg.muted" flexShrink={0}>
                    {headers[cellIdx]}
                  </Text>
                  <Box
                    fontSize="xs"
                    color="fg.default"
                    textAlign="end"
                    maxWidth="60%"
                    truncate
                    css={numericColumns[cellIdx] ? TABULAR_NUMS : undefined}
                  >
                    {renderCell(cell)}
                  </Box>
                </Flex>
              ))}
            </Box>
          ))}
        </VStack>
      </Box>

      {/* Row count and show more/less */}
      <Flex
        paddingX="2"
        paddingY="1"
        background="bg.muted"
        borderTop="1px solid"
        borderColor="border.muted"
        align="center"
        justify="between"
      >
        <Text fontSize="xs" color="fg.muted">
          {t('dataTable.rowCount', { count: rows.length })}
          {hasMoreRows && !showAll && ` ${t('dataTable.showing', { count: INITIAL_VISIBLE_ROWS })}`}
        </Text>
        {hasMoreRows && (
          <Button
            aria-label={showAll ? t('dataTable.showLess') : t('dataTable.showMore')}
            size="sm"
            variant="ghost"
            onClick={() => setShowAll((prev) => !prev)}
          >
            {showAll ? <LuChevronUp size={14} /> : <LuChevronDown size={14} />}
            {showAll ? t('dataTable.showLess') : t('dataTable.showMore')}
          </Button>
        )}
      </Flex>
    </Box>
  );
});

export default DataTableView;
